/**
 * Variant media grouping.
 *
 * Shopify allows only one featured image per variant. Merchants that need several
 * photos per color tag the extra media with "#ColorName" at the end of the image
 * alt text ("Front view #Black"). Every media item stays in the DOM and is shown or
 * hidden here when the selected color changes. Media without a "#" tag is shared and
 * always visible.
 *
 * Liquid renders the initial state, marks the gallery root with
 * data-alt-media-grouping="true" and puts the handleized group of each item in
 * data-media-group. This script only keeps that state in sync with the variant picker.
 */
(function () {
  const GALLERY_SELECTOR =
    '.product__media-wrapper[data-alt-media-grouping="true"]';
  const MODAL_SELECTOR = 'product-modal[data-alt-media-grouping="true"]';

  const HIDDEN_CLASS = {
    item: "product__media-item--variant-hidden",
    subitem: "product__media-subitem--variant-hidden",
    modalItem: "product-media-modal__item--variant-hidden",
  };

  const THUMBS_HIDDEN_CLASS = "product__media-sublist-outer--variant-hidden";
  const PRODUCT_ROOT_SELECTOR =
    ".product-section, .featured-product-section, .quick-add-modal__content-wrapper";

  // Mirrors the Liquid "handle" filter closely enough for option values.
  const handleize = (value) => {
    return String(value == null ? "" : value)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const getSectionId = (root) => {
    if (!root) return "";
    if (root.dataset.section) {
      return root.dataset.section.replace("product-media-", "");
    }
    return (root.id || "").replace("ProductModal-", "");
  };

  const getGalleries = (sectionId) => {
    return Array.from(
      document.querySelectorAll(
        `${GALLERY_SELECTOR}[data-section="product-media-${sectionId}"]`
      )
    );
  };

  const getModals = (sectionId) => {
    const modal = document.getElementById(`ProductModal-${sectionId}`);
    return modal && modal.dataset.altMediaGrouping === "true" ? [modal] : [];
  };

  const getSelectedVariant = (sectionId) => {
    const picker = document.getElementById(`variant-picker-${sectionId}`);
    const script = picker?.querySelector("[data-selected-variant]");
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  // An empty group key means "show everything", which is also the safe fallback
  // whenever the variant does not expose the color option.
  const resolveGroupKey = (root, variant) => {
    const position = parseInt(root.dataset.groupOptionPosition, 10);

    if (!variant || !Array.isArray(variant.options) || !(position > 0)) {
      return root.dataset.selectedMediaGroup || "";
    }

    return handleize(variant.options[position - 1]);
  };

  const setVisibility = (items, hiddenClass, groupKey) => {
    let visibleCount = 0;

    items.forEach((item) => {
      const group = (item.dataset.mediaGroup || "").trim();
      const isVisible = group === "" || groupKey === "" || group === groupKey;

      item.classList.toggle(hiddenClass, !isVisible);

      // A hidden Swiper slide still takes part in the layout calculations, so the
      // slide class is removed while the item is filtered out.
      if (item.parentElement?.classList.contains("swiper-wrapper")) {
        item.classList.toggle("swiper-slide", isVisible);
      }

      if (isVisible) visibleCount += 1;
    });

    return visibleCount;
  };

  // Swiper must never be destroyed and recreated here: that resets the gallery
  // layout (thumbs position, direction, heights). Updating in place keeps it.
  const refreshSwiper = (swiper) => {
    if (!swiper || swiper.destroyed) return;

    const wasVertical = swiper.isVertical();

    swiper.updateSize();
    swiper.updateSlides();
    swiper.updateProgress();
    swiper.updateSlidesClasses();
    swiper.update();

    if (wasVertical !== swiper.isVertical()) {
      swiper.changeDirection(wasVertical ? "vertical" : "horizontal", false);
    }
  };

  const slideToMedia = (swiper, datasetKey, targetId) => {
    if (!swiper || swiper.destroyed) return;

    const slides = Array.from(swiper.slides || []);
    if (slides.length === 0) return;

    let index = -1;
    if (targetId) {
      index = slides.findIndex(
        (slide) => slide.dataset?.[datasetKey] === targetId
      );
    }
    if (index === -1) index = 0;

    swiper.slideTo(index, 0);

    return index;
  };

  // Reproduces the height sync that subSliderInit sets up for the aside thumbs column.
  const syncThumbsHeight = (root) => {
    const mainEl = root.querySelector(".js-media-list, .js-media-list-mobile");
    const thumbsEl = root.querySelector(".product__media-sublist");
    if (!mainEl || !thumbsEl) return;

    const breakpoint = thumbsEl.classList.contains("sublist-thumbs-aside")
      ? 1100
      : 990;
    const observed = root.closest(PRODUCT_ROOT_SELECTOR) || root;

    if (observed.getBoundingClientRect().width < breakpoint) return;

    thumbsEl.style.height = `${mainEl.getBoundingClientRect().height - 1}px`;
  };

  const updateGallery = (root, groupKey, variant, sectionId) => {
    root.dataset.selectedMediaGroup = groupKey;

    setVisibility(
      root.querySelectorAll(".product__media-item[data-media-group]"),
      HIDDEN_CLASS.item,
      groupKey
    );

    const visibleThumbs = setVisibility(
      root.querySelectorAll(".product__media-subitem[data-media-group]"),
      HIDDEN_CLASS.subitem,
      groupKey
    );

    const thumbsOuter = root.querySelector(".product__media-sublist-outer");
    if (thumbsOuter) {
      thumbsOuter.classList.toggle(THUMBS_HIDDEN_CLASS, visibleThumbs <= 1);
    }

    const thumbsEl = root.querySelector(".js-media-sublist");
    const mainEl = root.querySelector(".js-media-list, .js-media-list-mobile");

    refreshSwiper(thumbsEl?.swiper);
    refreshSwiper(mainEl?.swiper);

    const targetId = variant?.featured_media
      ? `${sectionId}-${variant.featured_media.id}`
      : null;

    const activeIndex = slideToMedia(mainEl?.swiper, "mediaId", targetId);
    if (typeof activeIndex === "number") {
      thumbsEl?.swiper?.slideTo(activeIndex, 0);
    }

    window.setTimeout(() => syncThumbsHeight(root), 400);
  };

  const updateModal = (modal, groupKey, variant, sectionId) => {
    modal.dataset.selectedMediaGroup = groupKey;

    setVisibility(
      modal.querySelectorAll(".product-media-modal__item[data-media-group]"),
      HIDDEN_CLASS.modalItem,
      groupKey
    );

    const sliderEl = modal.querySelector(".js-popup-slider");
    refreshSwiper(sliderEl?.swiper);

    const targetId = variant?.featured_media
      ? `${sectionId}-${variant.featured_media.id}`
      : null;

    slideToMedia(sliderEl?.swiper, "mediaModalId", targetId);
  };

  /**
   * Filters the gallery of a product section down to the media of the selected color.
   *
   * @param {string|HTMLElement} section - Section id, or any gallery/modal root of it
   * @param {object} [variant] - Selected variant JSON; the rendered state is kept when omitted
   * @returns {boolean} Whether a gallery with grouping enabled was found
   */
  window.updateVariantMediaDisplay = function (section, variant) {
    const sectionId =
      typeof section === "string" ? section : getSectionId(section);
    if (!sectionId) return false;

    const galleries = getGalleries(sectionId);
    const modals = getModals(sectionId);
    if (galleries.length === 0 && modals.length === 0) return false;

    const groupKey = resolveGroupKey(galleries[0] || modals[0], variant);

    galleries.forEach((gallery) => {
      updateGallery(gallery, groupKey, variant, sectionId);
    });

    modals.forEach((modal) => {
      updateModal(modal, groupKey, variant, sectionId);
    });

    return true;
  };

  const init = () => {
    const roots = document.querySelectorAll(
      `${GALLERY_SELECTOR}, ${MODAL_SELECTOR}`
    );

    new Set(Array.from(roots, getSectionId)).forEach((sectionId) => {
      if (!sectionId) return;
      window.updateVariantMediaDisplay(
        sectionId,
        getSelectedVariant(sectionId)
      );
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("load", init);
  document.addEventListener("shopify:section:load", init);
})();
